import { DefaultTFuncReturn } from 'i18next';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useGetModelUsers } from '../../../../api/masters/masters';
import {
  ProductionBasicInfo,
  ProductionEvent,
  ProductionParentChild,
  ProductionPreparationItem,
} from '../../../../api/model';
import { useGetProductionDetail, useGetProductionMasters } from '../../../../api/productions/productions';
import { useGetSectionFirstId } from '../../../../api/sections/sections';
import { useDefaultOptions } from '../../../../hooks/api';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import { useEditModeSetValue } from '../../../layouts/LoginedLayout';
import ProductionApprovalApplicationsTab from './ProductionApprovalApplicationsTab';
import ProductionCommentsTab from './ProductionCommentsTab';
import ProductionConfirmationRequestsTab, {
  ProductionConfirmationRequestsTabHandler,
} from './ProductionConfirmationRequestsTab';
import ProductionManagementTableTab, { ProductionManagementTableTabHandler } from './ProductionManagementTableTab';
import ProductionManuscriptsTab from './ProductionManuscriptsTab';
import ProductionScheduleTab, { ProductionsScheduleTabHandler } from './ProductionScheduleTab';
import ProductionTossMaterialsTab, { ProductionTossMaterialsTabHandler } from './ProductionTossMaterialsTab';
import ProductionTranslationsTab from './ProductionTranslationsTab';

interface TabRadioProps {
  index: number;
  selectedTabIndex: number;
  isDisabled: boolean;
  setSelectedTabIndex: (next: number) => void;
}

/**
 * 制作管理画面のタブ切り替えに利用するラジオボタン
 * @param param0
 * @returns
 */
function TabRadio({ index, selectedTabIndex, isDisabled, setSelectedTabIndex }: TabRadioProps) {
  return (
    <input
      id={`pro-tab-btn${index}`}
      className="pro-tab-btn"
      name="pro-tab"
      type="radio"
      checked={selectedTabIndex === index}
      disabled={isDisabled}
      onChange={() => setSelectedTabIndex(index)}
      style={{ cursor: isDisabled ? 'default' : 'pointer' }}
    />
  );
}

interface TabLiProps {
  index: number;
  selectedTabIndex: number;
  label: DefaultTFuncReturn;
  isDisabled: boolean;
}

/**
 * 制作管理画面の上部のタブ部分
 * @param param0
 * @returns
 */
function TabLi({ index, selectedTabIndex, label, isDisabled }: TabLiProps) {
  return (
    <li>
      <label
        id={`pro-tab-list${index}`}
        className={`pro-tab-list ${selectedTabIndex === index ? 'current' : ''}`}
        htmlFor={`pro-tab-btn${index}`}
        tabIndex={isDisabled ? undefined : 0}
        onKeyDown={(e) => {
          if (e.code === 'Space') {
            // TODO: document.getElementById を使わないようにする
            document.getElementById(`pro-tab-list${index}`)?.click();
            e.preventDefault();
          }
        }}
        style={{
          color: isDisabled && selectedTabIndex !== index ? '#adadad' : '#333333',
          cursor: isDisabled ? 'default' : 'pointer',
        }}
      >
        {label}
      </label>
    </li>
  );
}

interface SwichingTabsProps {
  productionId: number | null;
  productionDivisionId: number;
  basicInfo: ProductionBasicInfo;
  event: ProductionEvent;
  preperationItem: ProductionPreparationItem;
  parentChildList: ProductionParentChild[];
  refetch: (() => Promise<void>) | undefined;
}

interface SwicingTabsHandler {
  refresh: () => void;
}

/**
 * 制作管理画面のタブ切り替えなどを含む部分
 */
export const SwitchingTabs = forwardRef<SwicingTabsHandler, SwichingTabsProps>(
  ({ productionId, productionDivisionId, basicInfo, event, preperationItem, parentChildList, refetch }, ref) => {
    // 言語情報
    const {
      t,
      i18n: { language },
    } = useTranslation();

    // クエリパラメータやハッシュ値に応じて、選択されているタブを変更する
    const { approvalApplicationTypeId, approvalApplicationId, confirmationRequestId, confirmationRequestDivisionId } =
      useParams();
    const { hash } = useLocation();
    const isApprovalApplicationEditMode = useMemo(
      () => approvalApplicationTypeId != null || approvalApplicationId != null,
      [approvalApplicationId, approvalApplicationTypeId]
    );
    const isConfirmationRequestEditMode = useMemo(
      () => confirmationRequestId != null || confirmationRequestDivisionId != null,
      [confirmationRequestDivisionId, confirmationRequestId]
    );
    const tabDisabled = useMemo(
      () => productionId == null || isApprovalApplicationEditMode || isConfirmationRequestEditMode,
      [isApprovalApplicationEditMode, isConfirmationRequestEditMode, productionId]
    );

    /* 翻訳タブの制御 */
    const tabDisabledTrans = useMemo(
      () =>
        productionId == null ||
        isApprovalApplicationEditMode ||
        isConfirmationRequestEditMode ||
        [1, 2, 3, 4, 5, 6, 13].includes(productionDivisionId) ||
        basicInfo.language_id === undefined ||
        basicInfo.language_id < 4,
      [isApprovalApplicationEditMode, isConfirmationRequestEditMode, productionId, productionDivisionId, basicInfo]
    );

    const initialTabIndex = useMemo((): number => {
      if (isApprovalApplicationEditMode) {
        return 8;
      }
      if (isConfirmationRequestEditMode) {
        return 4;
      }
      if (hash === '#schedule') {
        return 2;
      }
      if (hash === '#toss') {
        return 3;
      }
      return 1;
    }, [hash, isApprovalApplicationEditMode, isConfirmationRequestEditMode]);
    const [selectedTabIndex, setSelectedTabIndex] = useState(initialTabIndex);

    // 編集状態のタブ
    const [editModeTabIndex, setEditModeTabIndex] = useState<number>();
    const setEditMode = useEditModeSetValue();

    useEffect(() => {
      setEditMode(editModeTabIndex != null);
    }, [editModeTabIndex, setEditMode]);

    // 選択中の資料区分
    const [selectedDivisionId, setSelectedDivisionId] = useState(productionDivisionId);

    // タブを操作するための ref
    const refManagementTableTab = useRef<ProductionManagementTableTabHandler>(null);
    const refScheduleTab = useRef<ProductionsScheduleTabHandler>(null);
    const refTossTab = useRef<ProductionTossMaterialsTabHandler>(null);
    const refConfirmationRequestsTab = useRef<ProductionConfirmationRequestsTabHandler>(null);

    // マスターデータを取得する
    const options = useDefaultOptions(language);
    const {
      data: mastersData,
      isLoading: mastersIsLoading,
      isError: mastersIsError,
      error: mastersError,
    } = useGetProductionMasters(options);
    const {
      data: organizationsData,
      isLoading: organizationsIsLoading,
      isError: organizationsIsError,
      error: organizationsError,
    } = useGetModelUsers(options);

    // 先頭L2セクションIDを取得
    const { refetch: refetchFirstId, data: dataFirstFd } = useGetSectionFirstId(
      { production_id: Number(productionId) },
      options
    );

    // 外部から操作できる関数
    useImperativeHandle(ref, () => ({
      refresh: () => {
        // タブをリフレッシュ
        refManagementTableTab.current?.refresh();
        refScheduleTab.current?.refresh();
        refTossTab.current?.refresh();
        refConfirmationRequestsTab.current?.refresh();
      },
    }));

    // 待ち状態
    if (mastersIsLoading || organizationsIsLoading) {
      return <WaitComponent />;
    }

    // エラー状態
    if (mastersIsError || organizationsIsError) {
      return (
        <>
          {mastersIsError && <ErrorComponent error={mastersError} />}
          {organizationsIsError && <ErrorComponent error={organizationsError} />}
        </>
      );
    }

    // データが存在しない場合
    if (mastersData.data.production_divisions == null || organizationsData.data.companies == null) {
      return <NoDataComponent />;
    }

    return (
      <section>
        <div className="docufr">
          <dl className="documentttl">
            <dt>{t('資料管理No：')}</dt>
            <dd>{basicInfo.production_no}</dd>
            <dt>{t('資料名：')}</dt>
            <dd>{basicInfo.title}</dd>
          </dl>
          <div className="dropdown-in">
            <div className="droplist4">
              <span className="dropdown">
                <select
                  onChange={(e) => {
                    setSelectedDivisionId(parseInt(e.target.value, 10));
                  }}
                  value={selectedDivisionId}
                  disabled={editModeTabIndex !== 1}
                >
                  {mastersData.data.production_divisions.map((x) => (
                    <option key={x.production_division_id} value={x.production_division_id}>
                      {x.production_division_name}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          </div>
        </div>
        <div className="pro-tab-area">
          {/* タブの選択状態を示す一群のラジオボタン */}
          <TabRadio
            index={1}
            selectedTabIndex={selectedTabIndex}
            isDisabled={tabDisabled}
            setSelectedTabIndex={setSelectedTabIndex}
          />
          <TabRadio
            index={2}
            selectedTabIndex={selectedTabIndex}
            isDisabled={tabDisabled}
            setSelectedTabIndex={setSelectedTabIndex}
          />
          <TabRadio
            index={3}
            selectedTabIndex={selectedTabIndex}
            isDisabled={tabDisabled}
            setSelectedTabIndex={setSelectedTabIndex}
          />
          <TabRadio
            index={4}
            selectedTabIndex={selectedTabIndex}
            isDisabled={tabDisabled}
            setSelectedTabIndex={setSelectedTabIndex}
          />
          <TabRadio
            index={5}
            selectedTabIndex={selectedTabIndex}
            isDisabled={tabDisabled}
            setSelectedTabIndex={setSelectedTabIndex}
          />
          <TabRadio
            index={6}
            selectedTabIndex={selectedTabIndex}
            isDisabled={tabDisabled}
            setSelectedTabIndex={setSelectedTabIndex}
          />
          <TabRadio
            index={7}
            selectedTabIndex={selectedTabIndex}
            isDisabled={tabDisabledTrans}
            setSelectedTabIndex={setSelectedTabIndex}
          />
          <TabRadio
            index={8}
            selectedTabIndex={selectedTabIndex}
            isDisabled={tabDisabled}
            setSelectedTabIndex={setSelectedTabIndex}
          />
          <div className="pagetabmenufr">
            <ul className="pro-tab-list-wrap">
              {/* タブ */}
              <TabLi
                index={1}
                selectedTabIndex={selectedTabIndex}
                label={t('制作管理表')}
                isDisabled={isApprovalApplicationEditMode || isConfirmationRequestEditMode}
              />
              <TabLi
                index={2}
                selectedTabIndex={selectedTabIndex}
                label={t('日程編集')}
                isDisabled={isApprovalApplicationEditMode || isConfirmationRequestEditMode}
              />
              <TabLi
                index={3}
                selectedTabIndex={selectedTabIndex}
                label={t('トス資料')}
                isDisabled={isApprovalApplicationEditMode || isConfirmationRequestEditMode}
              />
              <TabLi
                index={4}
                selectedTabIndex={selectedTabIndex}
                label={t('確認依頼')}
                isDisabled={isApprovalApplicationEditMode || isConfirmationRequestEditMode}
              />
              <TabLi
                index={5}
                selectedTabIndex={selectedTabIndex}
                label={t('原稿')}
                isDisabled={isApprovalApplicationEditMode || isConfirmationRequestEditMode}
              />
              <TabLi
                index={6}
                selectedTabIndex={selectedTabIndex}
                label={t('懸案項目')}
                isDisabled={isApprovalApplicationEditMode || isConfirmationRequestEditMode}
              />
              <TabLi index={7} selectedTabIndex={selectedTabIndex} label={t('翻訳')} isDisabled={tabDisabledTrans} />
              <TabLi
                index={8}
                selectedTabIndex={selectedTabIndex}
                label={t('承認')}
                isDisabled={isApprovalApplicationEditMode || isConfirmationRequestEditMode}
              />
            </ul>
            <div className="keepfr">
              {/* 保存ボタン */}
              <span className="keepbtn">
                <input
                  id=""
                  name=""
                  type="submit"
                  value={t('保存').toString()}
                  className="searchbtn"
                  disabled={![1, 3].includes(editModeTabIndex ?? 0)}
                  onClick={async (e) => {
                    e.preventDefault();
                    if (editModeTabIndex === 1) {
                      await refManagementTableTab.current?.submit();
                    } else if (editModeTabIndex === 3) {
                      await refTossTab.current?.submit();
                    }
                  }}
                />
              </span>
            </div>
          </div>
          <div className="pro-tab-content-wrap wfr">
            {/* 制作管理表タブ */}
            <div id="pro-tab-content1" className="pro-tab-content productlistfr">
              <ProductionManagementTableTab
                ref={refManagementTableTab}
                productionId={productionId}
                selectedDivisionId={selectedDivisionId}
                basicInfo={basicInfo}
                event={event}
                preperationItem={preperationItem}
                parentChildList={parentChildList}
                editModeTabIndex={editModeTabIndex}
                masters={mastersData.data}
                companyTrees={organizationsData.data.companies}
                changeToEditMode={() => setEditModeTabIndex(1)}
                changeToViewMode={() => setEditModeTabIndex(undefined)}
                refetch={refetch}
                reset={() => setSelectedDivisionId(productionDivisionId)}
              />
            </div>
            {/* 制作管理表タブ以外のタブは編集モードのみ利用する */}
            {productionId != null && (
              <>
                {/* 日程編集タブ */}
                <div id="pro-tab-content2" className="pro-tab-content productlistfr">
                  <ProductionScheduleTab
                    ref={refScheduleTab}
                    productionId={productionId}
                    productionDivisionId={selectedDivisionId}
                    masters={mastersData.data}
                    editModeTabIndex={editModeTabIndex}
                    setEditable={(editable) => {
                      setEditModeTabIndex(editable ? 2 : undefined);
                    }}
                  />
                </div>
                {/* トス資料タブ */}
                <div id="pro-tab-content3" className="pro-tab-content productlistfr">
                  <ProductionTossMaterialsTab
                    ref={refTossTab}
                    productionId={productionId}
                    masters={mastersData.data}
                    editModeOuterTabIndex={editModeTabIndex}
                    setEditable={(editable) => {
                      if (editModeTabIndex == null && editable) {
                        setEditModeTabIndex(3);
                      } else if (editModeTabIndex === 3 && !editable) {
                        setEditModeTabIndex(undefined);
                      }
                    }}
                  />
                </div>
                {/* 確認依頼タブ */}
                <div id="pro-tab-content4" className="pro-tab-content productlistfr">
                  <ProductionConfirmationRequestsTab
                    productionId={productionId}
                    masters={mastersData.data}
                    companyTrees={organizationsData.data.companies}
                    editModeTabIndex={editModeTabIndex}
                    setEditable={(editable) => {
                      setEditModeTabIndex(editable ? 4 : undefined);
                    }}
                    ref={refConfirmationRequestsTab}
                  />
                </div>
                {/* 原稿タブ */}
                <div id="pro-tab-content5" className="pro-tab-content productlistfr">
                  <ProductionManuscriptsTab
                    productionId={productionId}
                    editModeTabIndex={editModeTabIndex}
                    setEditable={(editable) => {
                      setEditModeTabIndex(editable ? 5 : undefined);
                    }}
                    refetchFirstId={refetchFirstId}
                    firstSectionId={dataFirstFd?.data.section_id}
                  />
                </div>
                {/* 懸案項目タブ */}
                <div id="pro-tab-content6" className="pro-tab-content productlistfr">
                  <ProductionCommentsTab productionId={productionId} masters={mastersData.data} />
                </div>
                {/* 翻訳タブ */}
                <div id="pro-tab-content7" className="pro-tab-content productlistfr">
                  <ProductionTranslationsTab productionId={productionId} refetchFirstId={refetchFirstId} />
                </div>
                {/* 承認タブ */}
                <div id="pro-tab-content8" className="pro-tab-content productlistfr">
                  <ProductionApprovalApplicationsTab productionId={productionId} masters={mastersData.data} />
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    );
  }
);

interface MainAreaProps {
  productionId: number;
}

/**
 * 制作管理画面（編集）の制作管理データなどの取得部分
 * @param param0
 * @returns
 */
function MainArea({ productionId }: MainAreaProps) {
  // 言語情報
  const {
    i18n: { language },
  } = useTranslation();

  // 制作管理データの取得
  const options = useDefaultOptions(language);
  const {
    isLoading,
    isError,
    data,
    refetch: _refetch,
    isFetching,
    error,
  } = useGetProductionDetail(productionId, options);

  // 子コンポーネントの ref
  const ref = useRef<SwicingTabsHandler>(null);

  // データの取得関数（_refetch では無駄に返り値があるため、除去）
  const refetch = useCallback(async () => {
    await _refetch();
  }, [_refetch]);

  // フェッチが完了したら、子コンポーネントをリフレッシュ
  useEffect(() => {
    if (!isFetching) {
      ref.current?.refresh();
    }
  }, [isFetching]);

  // 待ち状態
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={error} />;
  }

  // データがない場合
  if (
    data.data.production_basic_info == null ||
    data.data.production_basic_info.production_division_id == null ||
    data.data.production_event == null ||
    data.data.production_preparation_item == null ||
    data.data.production_parent_child == null
  ) {
    return <NoDataComponent />;
  }

  return (
    <SwitchingTabs
      ref={ref}
      productionId={productionId}
      productionDivisionId={data.data.production_basic_info.production_division_id}
      basicInfo={data.data.production_basic_info}
      event={data.data.production_event}
      preperationItem={data.data.production_preparation_item}
      parentChildList={data.data.production_parent_child}
      refetch={refetch}
    />
  );
}

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|制作管理')}</title>
    </Helmet>
  );
}

/**
 * 制作管理画面（編集）ページのコンポーネント
 * @returns
 */
function ProductionsEditPage() {
  // 言語情報
  const { t } = useTranslation();

  // URL パラメータの取得
  const { productionId: productionIdString } = useParams();

  // 表示対象の制作管理 ID
  const productionId = useMemo(
    () => (productionIdString != null ? parseInt(productionIdString, 10) : undefined),
    [productionIdString]
  );

  // unmount 時に編集モードをやめる
  const setEditMode = useEditModeSetValue();
  useEffect(() => () => setEditMode(false), [setEditMode]);

  // 制作管理 ID が設定されていない場合
  if (productionId == null) {
    return (
      <div id="main">
        <Title />
        {t('この URL は存在しません。')}
      </div>
    );
  }

  // 通常の表示内容
  return (
    <div id="main">
      <Title />
      <MainArea productionId={productionId} />
    </div>
  );
}

export default ProductionsEditPage;
